import { type ReactNode, useState } from 'react';
import { Card, Box, Collapse, Divider, IconButton, Tooltip, alpha } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';

interface ExpandableCardProps {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  expandable?: boolean;
  defaultExpanded?: boolean;
  /** Controlled mode: externally managed expanded state */
  expanded?: boolean;
  /** Controlled mode: called when the user toggles the card */
  onToggle?: () => void;
  /** Tint the card background slightly, for zebra-striping alternate rows in a list */
  striped?: boolean;
}

export function ExpandableCard({
  title,
  actions,
  children,
  expandable = true,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onToggle,
  striped = false,
}: ExpandableCardProps) {
  const { t: tc } = useTranslation('common');
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;

  const handleToggle = () => {
    if (!expandable) return;
    if (isControlled) {
      onToggle?.();
    } else {
      setUncontrolledExpanded((prev) => !prev);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: 'visible',
        borderColor: (theme) =>
          expanded ? alpha(theme.palette.text.primary, 0.23) : theme.palette.divider,
        bgcolor: striped ? (theme) => alpha(theme.palette.text.primary, 0.035) : undefined,
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Box
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          cursor: expandable ? 'pointer' : 'default',
          '&:hover': expandable ? { bgcolor: 'action.hover' } : undefined,
          transition: 'background-color 0.15s',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          {title}
        </Box>
        {actions && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {actions}
          </Box>
        )}
        {expandable && (
          <Tooltip title={expanded ? tc('collapse') : tc('expand')}>
            <IconButton
              size="small"
              sx={{ ml: 0.5 }}
              aria-label={expanded ? tc('collapse') : tc('expand')}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {(expandable || expanded) && (
        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ p: 2 }}>{children}</Box>
        </Collapse>
      )}
    </Card>
  );
}
